// React
import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import {
  createFleetVehicleExpenseAPI,
  getFleetVehicleExpenseEditFormAPI,
  updateFleetVehicleExpenseAPI,
} from "services/api/fleet/vehicles/expenses/api";
import { initialFleetVehicleExpenseEditForm } from "services/api/fleet/vehicles/expenses/consts";
import { FleetVehicleExpenseEditFormValues } from "services/api/fleet/vehicles/expenses/types";
import { ExpenseAppliesTo } from "services/api/finance/expense-types/types";
import {
  useHandleDateChange,
  useHandleDropdownChange,
  useHandleNumberFieldChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";

// UI
import { Button } from "ui/components/Button/Button";
import { DatePicker } from "ui/components/DatePicker";
import ExpenseTypeDropdown from "ui/components/Dropdown/ExpenseTypeDropdown/ExpenseTypeDropdown";
import VehicleDropdown from "ui/components/Dropdown/VehicleDropdown/VehicleDropdown";
import OfficeDropdown from "ui/components/Dropdown/OfficeDropdown/OfficeDropdown";
import { Loader } from "ui/components/Loader";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { NumberField } from "ui/components/TextField/NumberField";
import { TextArea } from "ui/components/TextField/TextArea";
import { TextField } from "ui/components/TextField/TextField";

import { EXPENSE_EDIT_FORM_SCHEMA } from "./validation";
import { INITIAL_EXPENSE_FORM } from "./consts";
import { ExpenseEditCardProps } from "./types";

/**
 * Unos troška s ekrana Troškovi. Vozilo je neobavezno — bez njega je to opći trošak tvrtke, a
 * odabir vozila sužava popis vrsta troška na one koje se nude za vozila.
 */
const ExpenseEditCard: React.FC<ExpenseEditCardProps> = (props) => {
  const { show, expenseId, onSuccess, onClose } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetVehicleExpenseEditFormValues>(INITIAL_EXPENSE_FORM);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);

  // Vrste troška za vozilo i za tvrtku nisu isti popis, pa odabir vozila poništava vrstu.
  const handleVehicleChange = useCallback((_event: any, _name: string, option: any) => {
    setFormValues((prev) => ({ ...prev, vehicleId: option.value, expenseTypeId: null }));
    setErrors((prev) => ({ ...prev, expenseTypeId: "" }));
  }, []);
  const handleDateChange = useHandleDateChange(setFormValues, formValues);
  const handleNumberFieldChange = useHandleNumberFieldChange(setFormValues, formValues);

  useEffect(() => {
    if (!show) return;

    setErrors({});

    if (!expenseId) {
      setFormValues({ ...INITIAL_EXPENSE_FORM, expenseDate: new Date() });
      return;
    }

    fetchExpense(expenseId);
  }, [show, expenseId]);

  const fetchExpense = async (id: string) => {
    setIsLoading(true);

    try {
      setFormValues(await getFleetVehicleExpenseEditFormAPI(id));
    } catch {
      // greška se prikazuje kroz snackbar
    }

    setIsLoading(false);
  };

  const handleSave = useCallback(async () => {
    if (!validateYup(formValues, EXPENSE_EDIT_FORM_SCHEMA, setErrors)) return;

    setIsSaving(true);

    try {
      if (formValues.id) {
        await updateFleetVehicleExpenseAPI(formValues.vehicleId ?? "", formValues.id, formValues, [], t);
      } else {
        await createFleetVehicleExpenseAPI(formValues.vehicleId ?? "", formValues, [], t);
      }
      onSuccess();
    } catch {
      // greška se prikazuje kroz snackbar
    }

    setIsSaving(false);
  }, [formValues, onSuccess, t]);

  return (
    <Modal show={show} onHide={onClose} position="centered" size="medium">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={formValues.id ? "Uređivanje troška" : "Novi trošak"}
        onHide={onClose}
      />
      <ModalBody>
        {isLoading && (
          <div className="relative h-[300px]">
            <Loader isLoading={true}>{null}</Loader>
          </div>
        )}

        <div className={isLoading ? "hidden" : "px-5 pt-4 pb-5 flex flex-col gap-3"}>
          <div className="flex gap-4">
            <div className="flex-1">
              <VehicleDropdown
                name="vehicleId"
                label="Vozilo"
                value={formValues.vehicleId}
                onChange={handleVehicleChange}
                placeholder="Bez vozila — trošak tvrtke"
                showAddNew={false}
              />
            </div>
            <div className="flex-1">
              <ExpenseTypeDropdown
                name="expenseTypeId"
                label="Vrsta troška"
                value={formValues.expenseTypeId}
                onChange={handleDropdownChange}
                appliesTo={formValues.vehicleId ? ExpenseAppliesTo.VEHICLE : ExpenseAppliesTo.COMPANY}
                error={errors.expenseTypeId}
                required
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <OfficeDropdown
                name="officeId"
                label="Poslovnica"
                value={formValues.officeId}
                onChange={handleDropdownChange}
              />
            </div>
            <div className="flex-1"></div>
          </div>

          <TextField
            type="text"
            name="name"
            label="Naziv troška"
            value={formValues.name}
            onChange={handleTextFieldChange}
            error={errors.name}
            required
          />

          <div className="flex gap-4">
            <div className="flex-1">
              <DatePicker
                name="expenseDate"
                label="Datum troška"
                value={formValues.expenseDate}
                onChange={handleDateChange}
                error={errors.expenseDate}
                required
              />
            </div>
            <div className="flex-1">
              <DatePicker
                name="paymentDate"
                label="Datum plaćanja"
                value={formValues.paymentDate}
                onChange={handleDateChange}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <NumberField
                name="amount"
                label="Iznos bez PDV-a"
                value={formValues.amount}
                onChange={handleNumberFieldChange}
                decimals={2}
                error={errors.amount}
                required
              />
            </div>
            <div className="flex-1">
              <NumberField
                name="taxAmount"
                label="PDV"
                value={formValues.taxAmount}
                onChange={handleNumberFieldChange}
                decimals={2}
              />
            </div>
          </div>

          <TextArea
            name="description"
            label="Opis"
            value={formValues.description}
            onChange={handleTextFieldChange}
            rows={3}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="px-4 py-3 flex justify-between gap-3">
          <Button variant="outlined" children="Zatvori" onClick={onClose} />
          <Button
            variant="primary"
            children="Spremi"
            onClick={handleSave}
            disabled={isSaving}
          />
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(ExpenseEditCard);
